import { useCallback, useRef, useEffect } from 'react';
import useAppStore from '@/stores/appStore';

/**
 * Hook that provides notification sound playback using Web Audio API.
 * No external sound files needed — generates a pleasant chime programmatically.
 */
export const useNotificationSound = () => {
  const soundEnabled = useAppStore((state) => state.soundEnabled);
  const toggleSound = useAppStore((state) => state.toggleSound);
  const audioCtxRef = useRef(null);

  // Pre-initialize or resume AudioContext on first user interaction
  useEffect(() => {
    const initAudio = () => {
      try {
        if (!audioCtxRef.current) {
          audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
        } else if (audioCtxRef.current.state === 'suspended') {
          audioCtxRef.current.resume().catch(() => {});
        }
        // Remove listeners once initialized and running
        if (audioCtxRef.current && audioCtxRef.current.state === 'running') {
          document.removeEventListener('click', initAudio);
          document.removeEventListener('keydown', initAudio);
        }
      } catch (e) {
        // Silent catch
      }
    };

    document.addEventListener('click', initAudio);
    document.addEventListener('keydown', initAudio);

    return () => {
      document.removeEventListener('click', initAudio);
      document.removeEventListener('keydown', initAudio);
    };
  }, []);

  const playNotificationSound = useCallback(() => {
    if (!soundEnabled) return;
    
    try {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = audioCtxRef.current;
      if (ctx.state === 'suspended') {
        ctx.resume().catch(() => {});
      }
      const now = ctx.currentTime;

      // Two-tone chime: pleasant notification sound
      const frequencies = [523.25, 659.25]; // C5, E5
      
      frequencies.forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        
        osc.type = 'sine';
        osc.frequency.setValueAtTime(freq, now);
        
        gain.gain.setValueAtTime(0, now + i * 0.15);
        gain.gain.linearRampToValueAtTime(0.3, now + i * 0.15 + 0.05);
        gain.gain.exponentialRampToValueAtTime(0.001, now + i * 0.15 + 0.4);
        
        osc.connect(gain);
        gain.connect(ctx.destination);
        
        osc.start(now + i * 0.15);
        osc.stop(now + i * 0.15 + 0.5);
      });
    } catch (e) {
      // Silent fail — some browsers may block audio
      console.warn('Failed to play notification sound:', e);
    }
  }, [soundEnabled]);

  return {
    soundEnabled,
    toggleSound,
    playNotificationSound,
  };
};
